/**
 * Refer & Earn page.
 *
 * Two halves:
 *   1. Your code   — the customer's referral code, with copy + native share.
 *   2. Your referrals — everyone they've referred, masked mobile + status +
 *                       reward points (points land once the referee completes
 *                       their first service).
 *
 * Reward amounts are intentionally NOT hard-coded in copy — the actual points
 * earned per referral come straight from the API (referrer_reward_points), so
 * the page stays correct even if the backend reward config changes.
 */
import * as React from 'react';
import { Link } from '@tanstack/react-router';
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Copy,
  Gift,
  Link2,
  Share2,
  Sparkles,
  Users,
} from 'lucide-react';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorMessage,
  Skeleton,
  useToast,
} from '@finmin/ui';
import {
  useReferralCode,
  useReferralStatus,
  type ReferralStatusItem,
} from '@/api/referrals';

const PALETTE_FOREST = 'hsl(var(--palette-forest, 176 72% 17%))';
const PALETTE_MINT   = 'hsl(var(--palette-mint,   154  38% 94%))';
const PALETTE_AMBER  = 'hsl(var(--palette-amber,   33 100% 50%))';

export function ReferralsPage() {
  const { toast } = useToast();
  const codeQ   = useReferralCode();
  const statusQ = useReferralStatus();

  const code      = codeQ.data?.referral_code ?? '';
  const referrals = statusQ.data?.referrals ?? [];
  const total     = statusQ.data?.total ?? referrals.length;

  const [copied, setCopied]         = React.useState(false);
  const [linkCopied, setLinkCopied] = React.useState(false);

  // Deep link to signup with the code pre-filled (/signup?ref=CODE) — the
  // signup route's validateSearch reads `ref` and prefills the form.
  const inviteUrl = React.useMemo(
    () => (code ? `${window.location.origin}/signup?ref=${encodeURIComponent(code)}` : ''),
    [code],
  );

  async function copyCode() {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Code copied', 'Share it with a friend.');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy', 'Copy the code manually.');
    }
  }

  async function copyLink() {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setLinkCopied(true);
      toast.success('Invite link copied', 'Anyone who opens it gets your code pre-filled.');
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error('Could not copy link', 'Try the Share button instead.');
    }
  }

  async function shareCode() {
    if (!code) return;
    const intro = `Join me on FinminOS for hassle-free taxes & compliance. Sign up with my referral code ${code}:`;
    // navigator.share isn't in older TS DOM libs / non-secure contexts — guard it.
    const nav = navigator as Navigator & {
      share?: (data: { title?: string; text?: string; url?: string }) => Promise<void>;
    };
    if (typeof nav.share === 'function') {
      try {
        // Pass the URL ONLY via `url` — not embedded in `text`. Many share
        // targets append `url` to `text`, so including it in both shows the
        // link twice.
        await nav.share({ title: 'FinminOS referral', text: intro, url: inviteUrl });
        return;
      } catch {
        // User dismissed the share sheet — fall through to clipboard.
      }
    }
    // Clipboard fallback has no `url` field, so embed the link in the text here.
    try {
      await navigator.clipboard.writeText(`${intro}\n${inviteUrl}`);
      toast.success('Invite copied', 'Paste it into any chat to share.');
    } catch {
      toast.error('Could not share', 'Copy your code and share it manually.');
    }
  }

  return (
    <main className="mx-auto max-w-3xl px-6 py-10 space-y-8">

      {/* Back nav */}
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors group"
      >
        <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" aria-hidden />
        Back to Dashboard
      </Link>

      {/* Header */}
      <div className="flex items-start gap-4">
        <div
          className="h-12 w-12 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: PALETTE_MINT }}
          aria-hidden
        >
          <Gift className="h-6 w-6" style={{ color: PALETTE_FOREST }} />
        </div>
        <div className="min-w-0">
          <h1 className="font-display font-medium text-2xl tracking-tight text-foreground">
            Refer &amp; Earn
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5 max-w-xl">
            Share your code with friends. You both earn reward points when they
            complete their first service on FinminOS.
          </p>
        </div>
      </div>

      {/* Referral code card */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-muted-foreground" aria-hidden />
            Your referral code
          </CardTitle>
        </CardHeader>
        <CardContent>
          {codeQ.error ? (
            <ErrorMessage message={codeQ.error.message} />
          ) : codeQ.isLoading ? (
            <Skeleton className="h-14 w-full rounded-xl" />
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              {/* Code chip */}
              <div
                className="flex-1 rounded-xl border-2 border-dashed px-4 py-3 text-center sm:text-left"
                style={{ borderColor: PALETTE_FOREST, backgroundColor: PALETTE_MINT }}
              >
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  Code
                </p>
                <p
                  className="font-mono text-2xl font-bold tracking-[0.2em] mt-0.5"
                  style={{ color: PALETTE_FOREST }}
                >
                  {code || '—'}
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={copyCode}
                  disabled={!code}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-50 hover:shadow-md active:scale-95"
                  style={{ backgroundColor: PALETTE_FOREST }}
                >
                  {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy code'}
                </button>
                <button
                  type="button"
                  onClick={copyLink}
                  disabled={!code}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 hover:bg-muted active:scale-95 border border-border text-foreground"
                >
                  {linkCopied ? (
                    <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />
                  ) : (
                    <Link2 className="h-4 w-4" aria-hidden />
                  )}
                  {linkCopied ? 'Link copied' : 'Copy link'}
                </button>
                <button
                  type="button"
                  onClick={shareCode}
                  disabled={!code}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 hover:bg-muted active:scale-95 border border-border text-foreground"
                >
                  <Share2 className="h-4 w-4" aria-hidden />
                  Share
                </button>
              </div>
            </div>
          )}

          {/* How it works */}
          <ol className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <HowItWorksStep n={1} title="Share your code" desc="Send it to friends signing up for FinminOS." />
            <HowItWorksStep n={2} title="They sign up" desc="They enter your code while creating their account." />
            <HowItWorksStep n={3} title="You both earn" desc="Points land once they finish their first service." />
          </ol>
        </CardContent>
      </Card>

      {/* Referrals list */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" aria-hidden />
            Your referrals
            {!statusQ.isLoading && !statusQ.error && total > 0 && (
              <Badge variant="secondary" className="ml-1">{total}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {statusQ.error ? (
            <ErrorMessage message={statusQ.error.message} />
          ) : statusQ.isLoading ? (
            <ReferralsSkeleton />
          ) : referrals.length === 0 ? (
            <div className="py-10 text-center">
              <div
                className="mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-3"
                style={{ backgroundColor: PALETTE_MINT }}
              >
                <Users className="h-6 w-6" style={{ color: PALETTE_FOREST }} aria-hidden />
              </div>
              <p className="text-sm font-medium text-foreground">No referrals yet</p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Share your code above to start earning reward points.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border -my-2">
              {referrals.map((r) => (
                <ReferralRow key={r.id} referral={r} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </main>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function HowItWorksStep({ n, title, desc }: { n: number; title: string; desc: string }) {
  return (
    <li className="rounded-xl border border-border bg-background/60 p-3">
      <span
        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white"
        style={{ backgroundColor: PALETTE_AMBER }}
      >
        {n}
      </span>
      <p className="text-sm font-medium text-foreground mt-2">{title}</p>
      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{desc}</p>
    </li>
  );
}

function ReferralRow({ referral }: { referral: ReferralStatusItem }) {
  const isCompleted = referral.status === 'completed';
  const name = referral.referee_name?.trim() || referral.referee_mobile_masked;
  const showMobileSub = !!referral.referee_name?.trim();

  return (
    <li className="flex items-center gap-3 py-3">
      <span
        className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
        style={{ backgroundColor: PALETTE_MINT, color: PALETTE_FOREST }}
        aria-hidden
      >
        {getInitials(name)}
      </span>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground truncate">{name}</p>
        <p className="text-xs text-muted-foreground">
          {showMobileSub && <span>{referral.referee_mobile_masked} · </span>}
          Joined {formatDate(referral.signup_at)}
        </p>
      </div>

      <div className="flex flex-col items-end gap-1 shrink-0">
        {isCompleted ? (
          <Badge className="gap-1 bg-success/10 text-success border-success/20">
            <CheckCircle2 className="h-3 w-3" aria-hidden />
            Completed
          </Badge>
        ) : (
          <Badge variant="outline" className="gap-1 text-muted-foreground">
            <Clock className="h-3 w-3" aria-hidden />
            Pending
          </Badge>
        )}
        {isCompleted && referral.referrer_reward_points != null && (
          <span className="text-xs font-semibold tabular-nums" style={{ color: PALETTE_FOREST }}>
            +{referral.referrer_reward_points.toLocaleString('en-IN')} pts
          </span>
        )}
      </div>
    </li>
  );
}

function ReferralsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-full" />
          <div className="flex-1">
            <Skeleton className="h-4 w-32 mb-1.5" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-6 w-20 rounded-full" />
        </div>
      ))}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  if (!name || name.startsWith('+')) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0]![0]!.toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}
